import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Alert,
} from '@mui/material';
import { TrendingUp, TrendingDown, Assessment } from '@mui/icons-material';
import { taxAnalysisAPI } from '../../api';

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

const DSCRSummary = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await taxAnalysisAPI.getDSCRSummary();
        setData(res.data.data || []);
      } catch (err) {
        if (err.response?.status !== 404) {
          setError('Failed to load DSCR data');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!data || data.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Assessment sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d2137' }}>
          DSCR Summary — Tax Returns
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Tax Year</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Gross Receipts</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Net Income</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Adj. Cash Flow</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Annual Debt Svc</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">DSCR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              const dscrColor = row.dscr >= 1.25 ? 'success' : row.dscr >= 1.0 ? 'warning' : 'error';
              return (
                <TableRow key={row.taxYear} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.taxYear}
                    </Typography>
                    {row.entityType && (
                      <Typography variant="caption" color="text.secondary">
                        Form {row.entityType}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(row.grossReceipts)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.netIncome)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.adjustedCashFlow)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.annualDebtService)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={row.dscr >= 1.0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                      label={row.dscr > 0 ? `${row.dscr.toFixed(2)}x` : 'N/A'}
                      color={dscrColor}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {data.length > 0 && data[0].dscr > 0 && (
        <Box sx={{ mt: 1.5, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            DSCR = (Net Income + Interest + Depreciation + Amortization + Officer Comp) / Annual Debt Service.
            {' '}A ratio above 1.25x is generally considered strong.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DSCRSummary;
